import { pointerIntersection } from "@dnd-kit/collision";
import { move } from "@dnd-kit/helpers";
import { DragDropProvider } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { t } from "@travily/i18n";
import type ImageType from "@travily/types/shared/Image";
import { ActionMenu } from "@ui/components/ActionMenu";
import { Icon } from "@ui/components/Icon";
import { Image } from "@ui/components/Image";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import type { AspectRatio } from "@ui/types/media";
import { useEffect, useRef, useState } from "react";

const styles = tv({
  slots: {
    root: "flex flex-col gap-3",
    grid: "grid grid-cols-2 gap-3",
    coverItem: [
      // bg-white backs transparent PNGs — without it the page shows through,
      // most visibly on the floating tile while dragging.
      "group relative col-span-2 overflow-hidden rounded-xl bg-white",
      // Hairline over the image edge so white-backed photos don't bleed into
      // the page; a ring on the tile itself would paint underneath the image.
      "after:pointer-events-none after:absolute after:inset-0 after:rounded-xl after:ring-1 after:ring-neutral-100 after:ring-inset",
    ],
    secondaryItem: [
      "group relative overflow-hidden rounded-xl bg-white",
      "after:pointer-events-none after:absolute after:inset-0 after:rounded-xl after:ring-1 after:ring-neutral-100 after:ring-inset",
    ],
    image: "size-full cursor-grab object-cover active:cursor-grabbing",
    coverBadge:
      "absolute top-3 start-3 z-[1] rounded-md bg-white px-2.5 py-1 text-xs font-bold text-neutral-900 shadow-sm",
    menuButton:
      "absolute top-3 end-3 z-10 flex size-8 cursor-pointer items-center justify-center rounded-full bg-white/90 text-neutral-600 opacity-0 shadow-sm outline-none backdrop-blur-sm transition-opacity hover:bg-white focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-neutral-400 group-hover:opacity-100",
    emptyPlaceholder:
      "flex cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-neutral-200 bg-neutral-50 outline-none hover:border-neutral-300 hover:bg-neutral-100 focus-visible:ring-2 focus-visible:ring-neutral-400",
    emptyPlaceholderIcon: "text-neutral-300",
    addTile:
      "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-neutral-200 bg-white text-neutral-500 outline-none hover:border-neutral-300 hover:bg-neutral-50 hover:text-neutral-600 focus-visible:ring-2 focus-visible:ring-neutral-400",
    addTileText: "text-sm font-medium",
  },
  variants: {
    aspectRatio: {
      square: {
        coverItem: "aspect-square",
        secondaryItem: "aspect-square",
        emptyPlaceholder: "aspect-square",
        addTile: "aspect-square",
      },
      video: {
        coverItem: "aspect-video",
        secondaryItem: "aspect-video",
        emptyPlaceholder: "aspect-video",
        addTile: "aspect-video",
      },
    },
    isMenuOpen: {
      true: { menuButton: "opacity-100" },
    },
  },
});

const EMPTY_SLOT_KEYS = ["slot-a", "slot-b", "slot-c", "slot-d", "slot-e"];

export interface OrganizerPhoto {
  id: string;
  image: ImageType | null;
  position: number;
}

export interface PhotoOrganizerProps {
  aspectRatio: AspectRatio;
  minPhotos: number;
  photos: OrganizerPhoto[];
  onAddMore: () => void;
  onDelete: (photoId: string) => void;
  onReorder: (orderedIds: string[]) => void;
}

interface SortablePhotoProps {
  aspectRatio: AspectRatio;
  photo: OrganizerPhoto;
  index: number;
  isCover: boolean;
  activeDragId: string | null;
  totalPhotos: number;
  onDelete: (photoId: string) => void;
  onMoveBackward: (photoId: string) => void;
  onMoveForward: (photoId: string) => void;
  onMakeCover: (photoId: string) => void;
}

function SortablePhoto({
  aspectRatio,
  photo,
  index,
  isCover,
  activeDragId,
  totalPhotos,
  onDelete,
  onMoveBackward,
  onMoveForward,
  onMakeCover,
}: SortablePhotoProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const s = styles({ aspectRatio, isMenuOpen });
  const { ref } = useSortable({
    id: photo.id,
    index,
    collisionDetector: pointerIntersection,
    transition: { idle: true, duration: 0, easing: "linear" },
  });

  const isDragging = activeDragId === photo.id;

  return (
    <div ref={ref} className={isCover ? s.coverItem() : s.secondaryItem()} data-dragging={isDragging || undefined}>
      <Image
        image={photo.image}
        alt=""
        sizes={isCover ? "(min-width: 768px) 768px, 100vw" : "(min-width: 768px) 380px, 50vw"}
        className={s.image()}
      />

      {isCover && <span className={s.coverBadge()}>{t("components.photo_organizer.cover_photo")}</span>}

      {!isDragging && (
        <ActionMenu
          items={[
            index > 0 && {
              id: "move-forward",
              label: t("components.photo_organizer.move_forward"),
              onAction: () => onMoveForward(photo.id),
            },
            index < totalPhotos - 1 && {
              id: "move-backward",
              label: t("components.photo_organizer.move_backward"),
              onAction: () => onMoveBackward(photo.id),
            },
            !isCover && {
              id: "make-cover",
              label: t("components.photo_organizer.make_cover"),
              onAction: () => onMakeCover(photo.id),
            },
            { type: "separator" as const },
            {
              id: "delete",
              label: t("components.photo_organizer.delete"),
              onAction: () => onDelete(photo.id),
              destructive: true,
            },
          ]}
          onOpenChange={setIsMenuOpen}
          placement="bottom end"
          trigger={
            <Button
              aria-label={t("components.photo_organizer.options")}
              className={s.menuButton()}
              onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
            >
              <Icon name="more" size="20" />
            </Button>
          }
        />
      )}
    </div>
  );
}

export function PhotoOrganizer({
  aspectRatio,
  minPhotos,
  photos: initialPhotos,
  onAddMore,
  onDelete,
  onReorder,
}: PhotoOrganizerProps) {
  const s = styles({ aspectRatio });
  const [photos, setPhotos] = useState(initialPhotos);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const photosRef = useRef(photos);
  const prevInitialPhotosRef = useRef(initialPhotos);

  // Keep ref in sync so DragDropProvider handlers always read fresh state
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  // Sync with props only when the SERVER data changes (new initialPhotos reference),
  // not when local drag reorder causes photos to differ from initialPhotos.
  if (prevInitialPhotosRef.current !== initialPhotos) {
    prevInitialPhotosRef.current = initialPhotos;
    setPhotos(initialPhotos);
  }

  const emptySlotCount = Math.max(0, minPhotos - photos.length);

  const applyReorder = (reorder: (items: OrganizerPhoto[]) => OrganizerPhoto[] | null) => {
    const newOrder = reorder([...photos]);
    if (!newOrder) return;
    setPhotos(newOrder);
    onReorder(newOrder.map((p) => p.id));
  };

  const handleMoveForward = (photoId: string) =>
    applyReorder((items) => {
      const i = items.findIndex((p) => p.id === photoId);
      if (i <= 0) return null;
      const prev = items[i - 1];
      const curr = items[i];
      if (!prev || !curr) return null;
      items[i - 1] = curr;
      items[i] = prev;
      return items;
    });

  const handleMoveBackward = (photoId: string) =>
    applyReorder((items) => {
      const i = items.findIndex((p) => p.id === photoId);
      if (i >= items.length - 1) return null;
      const curr = items[i];
      const next = items[i + 1];
      if (!curr || !next) return null;
      items[i] = next;
      items[i + 1] = curr;
      return items;
    });

  const handleMakeCover = (photoId: string) =>
    applyReorder((items) => {
      const i = items.findIndex((p) => p.id === photoId);
      if (i <= 0) return null;
      const item = items.splice(i, 1)[0];
      if (!item) return null;
      items.unshift(item);
      return items;
    });

  return (
    <DragDropProvider
      onDragStart={(event) => {
        const sourceId = (event.operation.source?.id as string) ?? null;
        setActiveDragId(sourceId);
      }}
      onDragOver={(event) => {
        const next = move(photosRef.current, event);
        if (next !== photosRef.current) setPhotos(next);
      }}
      onDragEnd={(event) => {
        if (!event.canceled) {
          const newPhotos = move(photosRef.current, event);
          setPhotos(newPhotos);
          onReorder(newPhotos.map((p) => p.id));
        }
        setActiveDragId(null);
      }}
    >
      <div className={s.root()}>
        <div className={s.grid()}>
          {photos.map((photo, index) => (
            <SortablePhoto
              key={photo.id}
              activeDragId={activeDragId}
              aspectRatio={aspectRatio}
              index={index}
              isCover={index === 0}
              onDelete={onDelete}
              onMakeCover={handleMakeCover}
              onMoveBackward={handleMoveBackward}
              onMoveForward={handleMoveForward}
              photo={photo}
              totalPhotos={photos.length}
            />
          ))}

          {EMPTY_SLOT_KEYS.slice(0, emptySlotCount).map((key) => (
            <Button
              key={key}
              aria-label={t("components.photo_organizer.add_photos")}
              className={s.emptyPlaceholder()}
              onPress={onAddMore}
            >
              <Icon name="image" size="32" className={s.emptyPlaceholderIcon()} />
            </Button>
          ))}

          <Button
            aria-label={t("components.photo_organizer.add_more_photos")}
            className={s.addTile()}
            onPress={onAddMore}
          >
            <Icon name="add" size="20" />
            <span className={s.addTileText()}>{t("components.photo_organizer.add_more")}</span>
          </Button>
        </div>
      </div>
    </DragDropProvider>
  );
}
